import React, { useEffect, useMemo } from 'react';
import { useTrainingHubHook } from '../../hooks/traininghub.hook';
import { Link, useLocation, useParams } from 'react-router-dom';
import { getCall } from '../../hooks/Network';
import { useTranslation } from 'react-i18next';
import { hasSubscription, hasSubscriptionData } from '../../hooks/Helper';
import { useDispatch, useSelector } from 'react-redux';
import { setBusinessHubId } from '../../features/authentication/authSlice';
import { getProductConfig } from '../../config/products';
import { findGreatestWeightedMatch } from '../../util/ArrayWorks';
import PropTypes from 'prop-types';
import configs from '../../config/app';
import useFlagsHook from '../../hooks/useFlags.hook';
import { DenyByFlagFilter } from '../../components/permission';
//"features/authentication/authSlice";

// Associate Back Office Component
const AssociateBackOffice = ({ subscriptions }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { customerId } = useParams();
  const location = useLocation();
  const ApiKey = process.env.REACT_APP_API_KEY;
  const clientId = Number(configs.clientId);
  const sessionProducts = getProductConfig(clientId, 'liveSessions') || [];
  const { getFlagValue } = useFlagsHook();
  const businessHubAffiliateId = useMemo(() => {
    if (!Array.isArray(subscriptions)) return 0;
    const allowedPlans = getProductConfig(clientId, 'businessHub');
    return hasSubscriptionData(subscriptions, allowedPlans)?.subscriptionId || 0;
  }, [subscriptions, clientId]);
  const { hubLink, ready } = useTrainingHubHook(customerId, subscriptions);

  useEffect(() => {
    if (businessHubAffiliateId) {
      dispatch(setBusinessHubId(businessHubAffiliateId));
      sessionStorage.setItem('BussinessHubId', businessHubAffiliateId);
    }
  }, [businessHubAffiliateId, dispatch]);

  // REDUX
  const sidebarExpanded = useSelector((state) => state.ui.sidebarExpanded);

  const hasRequiredLumen = hasSubscription(
    subscriptions,
    getProductConfig(clientId, 'lumenHub')
  );
  const hasRequiredAffiliateMarketingKit = hasSubscription(
    subscriptions,
    getProductConfig(clientId, 'marketingKitHub')
  );
  const hasBusinessHub = hasSubscription(
    subscriptions,
    getProductConfig(clientId, 'businessHub')
  );
  const showBusinessHub =
    hasRequiredAffiliateMarketingKit || (clientId === 3 && hasBusinessHub);
  const hasRequiredAcademy = hasSubscription(
    subscriptions,
    getProductConfig(clientId, 'academyHub')
  );
  const hasRequiredForex = hasSubscription(
    subscriptions,
    getProductConfig(clientId, 'forexHub')
  );

  const fnEcaSso = async (e) => {
    e.preventDefault();

    let sParams = {
      url: 'getSSOToken',
      urlParams: { AssociateId: customerId },
      headers: { 'API-key': ApiKey },
    };
    try {
      const resp = await getCall(sParams);
      if (resp.status === 200 && resp.data) {
        const serviceItem =
          findGreatestWeightedMatch(sessionProducts, subscriptions)?.shortCode || '';
        const envUrl =
          getFlagValue('NEW_ACADEMY_REDIRECT') === '1'
            ? `https://www.noa.training/sso-login`
            : process.env.REACT_APP_FLAG_ACADEMY_URL;
        // ?token=${token}&clientId=${configs.clientId}${serviceItem ? `&service=${serviceItem}` : ''};
        // const envUrl = process.env.REACT_APP_FLAG_ACADEMY_URL;
        // const ssoUrl = `${envUrl}?token=${resp.data}`;
        const ssoUrl = new URL(envUrl);
        ssoUrl.searchParams.set('token', resp.data);
        ssoUrl.searchParams.set('clientId', clientId);

        if (serviceItem && getFlagValue('NEW_ACADEMY_REDIRECT') === '1') {
          ssoUrl.searchParams.set('service', serviceItem);
        }
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

        // Try opening in new tab
        const newWindow = window.open(ssoUrl, '_blank', 'noopener,noreferrer');

        // If blocked
        if (
          isSafari &&
          (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined')
        ) {
          alert(t('popWasBlocked'));
        }
      } else {
        console.error('Error:', resp);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  return (
    <>
      <li
        className={`nav-item ${location.pathname == `/customers/${customerId}/dashboard` ? 'active' : ''}`}
      >
        <Link className='nav-link' to={`/customers/${customerId}/dashboard`}>
          <span className='nav-link-icon d-md-none d-lg-inline-block'>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              className='icon icon-thick icon-tabler icon-tabler-layout-dashboard'
              width='40'
              height='40'
              viewBox='0 0 22 22'
              strokeWidth='1'
              stroke='currentColor'
              fill='none'
              strokeLinecap='round'
              strokeLinejoin='round'
            >
              <path stroke='none' d='M0 0h24v24H0z' fill='none'></path>
              <path d='M4 4h6v8h-6z'></path>
              <path d='M4 16h6v4h-6z'></path>
              <path d='M14 12h6v8h-6z'></path>
              <path d='M14 4h6v4h-6z'></path>
            </svg>
          </span>
          <span className={`nav-link-title ${!sidebarExpanded ? 'hide-text' : ''}`}>
            {t('dashboard')}
          </span>
        </Link>
      </li>

      <li
        className={`nav-item ${location.pathname == `/customers/${customerId}/order-subscription` ? 'active' : ''}`}
      >
        <Link className='nav-link' to={`/customers/${customerId}/order-subscription`}>
          <span className='nav-link-icon d-md-none d-lg-inline-block'>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='40'
              height='40'
              viewBox='0 0 22 22'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
              className='icon icon-thick icon-tabler icons-tabler-outline icon-tabler-device-desktop-cog'
            >
              <path stroke='none' d='M0 0h24v24H0z' fill='none' />
              <path d='M12 16h-8a1 1 0 0 1 -1 -1v-10a1 1 0 0 1 1 -1h16a1 1 0 0 1 1 1v7' />
              <path d='M7 20h5' />
              <path d='M9 16v4' />
              <path d='M19.001 19m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0' />
              <path d='M19.001 15.5v1.5' />
              <path d='M19.001 21v1.5' />
              <path d='M22.032 17.25l-1.299 .75' />
              <path d='M17.27 20l-1.3 .75' />
              <path d='M15.97 17.25l1.3 .75' />
              <path d='M20.733 20l1.3 .75' />
            </svg>
          </span>
          <span className={`nav-link-title ${!sidebarExpanded ? 'hide-text' : ''}`}>
            {t('order&Subscription')}
          </span>
        </Link>
      </li>

      <li
        className={`nav-item ${location.pathname == `/customers/${customerId}/media` ? 'active' : ''}`}
      >
        <Link className='nav-link' to={`customers/${customerId}/media`}>
          <span className='nav-link-icon d-md-none d-lg-inline-block'>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              className='icon icon-thick'
              width='40'
              height='40'
              viewBox='0 0 22 22'
              strokeWidth='1'
              stroke='currentColor'
              fill='none'
              strokeLinecap='round'
              strokeLinejoin='round'
            >
              <path stroke='none' d='M0 0h24v24H0z' fill='none'></path>
              <path d='M15 3v4a1 1 0 0 0 1 1h4'></path>
              <path d='M18 17h-7a2 2 0 0 1 -2 -2v-10a2 2 0 0 1 2 -2h4l5 5v7a2 2 0 0 1 -2 2z'></path>
              <path d='M16 17v2a2 2 0 0 1 -2 2h-7a2 2 0 0 1 -2 -2v-10a2 2 0 0 1 2 -2h2'></path>
            </svg>
          </span>
          <span className={`nav-link-title ${!sidebarExpanded ? 'hide-text' : ''}`}>
            {t('documents')}
          </span>
        </Link>
      </li>

      {showBusinessHub && (
        <DenyByFlagFilter
          flagName='BUSINESS_HUB_FILTER'
          focusValue={'account.associateId'}
        >
          <li
            className={`nav-item ${location.pathname == `/customers/${customerId}/business-hub` ? 'active' : ''}`}
          >
            <Link className='nav-link' to={`/customers/${customerId}/business-hub`}>
              <span className='nav-link-icon d-md-none d-lg-inline-block'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='40'
                  height='40'
                  viewBox='0 0 22 22'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  className='icon icon-thick icon-tabler icons-tabler-outline icon-tabler-world-dollar'
                >
                  <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                  <path d='M20.876 10.51a9 9 0 1 0 -7.839 10.43' />
                  <path d='M3.6 9h16.8' />
                  <path d='M3.6 15h9.9' />
                  <path d='M11.5 3a17 17 0 0 0 0 18' />
                  <path d='M12.5 3a16.986 16.986 0 0 1 2.578 9.02' />
                  <path d='M21 15h-2.5a1.5 1.5 0 0 0 0 3h1a1.5 1.5 0 0 1 0 3h-2.5' />
                  <path d='M19 21v1m0 -8v1' />
                </svg>
              </span>
              <span className={`nav-link-title ${!sidebarExpanded ? 'hide-text' : ''}`}>
                {t('businessHub')}
              </span>
            </Link>
          </li>
        </DenyByFlagFilter>
      )}

      {hasRequiredAcademy && ready && hubLink && (
        <li
          className={`nav-item ${location.pathname == `/customers/${customerId}/eca` ? 'active' : ''}`}
        >
          <Link
            to={hubLink}
            className='nav-link'
            target='_blank'
            rel='noopener noreferrer'
          >
            <span className='nav-link-icon d-md-none d-lg-inline-block'>
              <svg
                xmlns=' (http://www.w3.org/2000/svg)http://www.w3.org/2000/svg'
                width='24'
                height='24'
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
                className='icon icon-thick icon-tabler icons-tabler-outline icon-tabler-bulb'
              >
                <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                <path d='M3 12h1m8 -9v1m8 8h1m-15.4 -6.4l.7 .7m12.1 -.7l-.7 .7' />
                <path d='M9 16a5 5 0 1 1 6 0a3.5 3.5 0 0 0 -1 3a2 2 0 0 1 -4 0a3.5 3.5 0 0 0 -1 -3' />
                <path d='M9.7 17l4.6 0' />
              </svg>
            </span>
            <span className={`nav-link-title ${!sidebarExpanded ? 'hide-text' : ''}`}>
              {t('ecaHub')}
            </span>
          </Link>
        </li>
      )}

      {hasRequiredForex && (
        <li
          className={`nav-item ${location.pathname == `/customers/${customerId}/forex` ? 'active' : ''}`}
        >
          <Link className='nav-link' to={`/customers/${customerId}/forex`}>
            <span className='nav-link-icon d-md-none d-lg-inline-block'>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='24'
                height='24'
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
                className='icon icon-thick icon-tabler icons-tabler-outline icon-tabler-currency-dollar'
              >
                <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                <path d='M16.7 8a3 3 0 0 0 -2.7 -2h-4a3 3 0 0 0 0 6h4a3 3 0 0 1 0 6h-4a3 3 0 0 1 -2.7 -2' />
                <path d='M12 3v3m0 12v3' />
              </svg>
            </span>
            <span className={`nav-link-title ${!sidebarExpanded ? 'hide-text' : ''}`}>
              {t('forexHub')}
            </span>
          </Link>
        </li>
      )}

      {hasRequiredLumen && (
        <li
          className={`nav-item ${location.pathname == `/customers/${customerId}/crypto` ? 'active' : ''}`}
        >
          <Link to={`/customers/${customerId}/crypto`} className='nav-link'>
            <span className='nav-link-icon d-md-none d-lg-inline-block'>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='24'
                height='24'
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
                className='icon icon-thick icon-tabler icons-tabler-outline icon-tabler-currency-bitcoin'
              >
                <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                <path d='M6 6h8a3 3 0 0 1 0 6a3 3 0 0 1 0 6h-8' />
                <path d='M8 6l0 12' />
                <path d='M8 12l6 0' />
                <path d='M9 3l0 3' />
                <path d='M13 3l0 3' />
                <path d='M9 18l0 3' />
                <path d='M13 18l0 3' />
              </svg>
            </span>
            <span className={`nav-link-title ${!sidebarExpanded ? 'hide-text' : ''}`}>
              {t('cryptoHub')}
            </span>
          </Link>
        </li>
      )}
    </>
  );
};

AssociateBackOffice.propTypes = {
  subscriptions: PropTypes.any.isRequired,
};

export default AssociateBackOffice;
